import { createTheme } from "@mui/material/styles";
import { createContext, useMemo, useState } from "react";

// Bitcode Enterprise Brand Colors
// Primary Cyan: #00D4FF
// Dark Blue: #0A1628
// Medium Blue: #1E3A5F
// Orange Accent: #FF8C00

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d5db",
          200: "#a1abb7",
          300: "#728193",
          400: "#1E3A5F", // Bitcode Medium Blue
          500: "#0A1628", // Bitcode Dark Blue (main background)
          600: "#08121f",
          700: "#060d17",
          800: "#04090f",
          900: "#020408",
        },
        green: {
          100: "#f0fff4",
          200: "#c6f6d5",
          300: "#9ae6b4",
          400: "#68d391",
          500: "#48bb78",
          600: "#38a169",
          700: "#2f855a",
          800: "#276749",
          900: "#22543d",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#70d8bd",
          500: "#4cceac",
          600: "#3da58a",
          700: "#2e7c67",
          800: "#1e5245",
          900: "#0f2922",
        },
        red: {
          100: "#fff5f5",
          200: "#fed7d7",
          300: "#feb2b2",
          400: "#fc8181",
          500: "#f56565",
          600: "#e53e3e",
          700: "#c53030",
          800: "#9b2c2c",
          900: "#742a2a",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blue: {
          100: "#e6faff",
          200: "#b3f0ff",
          300: "#80e6ff",
          400: "#4ddbff",
          500: "#00D4FF", // Bitcode Cyan (main accent)
          600: "#00a8cc",
          700: "#007d99",
          800: "#005266",
          900: "#002933",
        },
        blueAccent: {
          100: "#e6faff",
          200: "#b3f0ff",
          300: "#80e6ff",
          400: "#4ddbff",
          500: "#00D4FF", // Bitcode Cyan
          600: "#00a8cc",
          700: "#007d99",
          800: "#005266",
          900: "#002933",
        },
        cyan: {
          100: "#e6faff",
          200: "#b3f0ff",
          300: "#80e6ff",
          400: "#4ddbff",
          500: "#00D4FF", // Bitcode Cyan (main)
          600: "#00a8cc",
          700: "#007d99",
          800: "#005266",
          900: "#002933",
        },
        orange: {
          100: "#fff3e0",
          200: "#ffe0b2",
          300: "#ffcc80",
          400: "#ffb74d",
          500: "#FF8C00", // Bitcode Orange
          600: "#cc7000",
          700: "#995400",
          800: "#663800",
          900: "#331c00",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          250: "#231F20",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#020408",
          200: "#04090f",
          300: "#060d17",
          400: "#f2f0f0", // light background
          450: "#ffffff",
          500: "#0A1628", // Bitcode Dark Blue
          600: "#1E3A5F", // Bitcode Medium Blue
          700: "#728193",
          800: "#a1abb7",
          900: "#d0d5db",
        },
        secondary: {
          100: "#e6faff",
          200: "#b3f0ff",
          300: "#80e6ff",
          400: "#4ddbff",
          500: "#00D4FF", // Bitcode Cyan
          600: "#00a8cc",
          650: "#4ddbff",
          700: "#007d99",
          800: "#005266",
          900: "#002933",
        },
        green: {
          100: "#f0fff4",
          150: "#e0ffce",
          200: "#c6f6d5",
          300: "#9ae6b4",
          400: "#68d391",
          500: "#48bb78",
          600: "#38a169",
          700: "#2f855a",
          750: "#49B707",
          800: "#276749",
          900: "#22543d",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#1e5245",
          300: "#2e7c67",
          400: "#3da58a",
          500: "#4cceac",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        red: {
          100: "#fff5f5",
          200: "#fed7d7",
          300: "#feb2b2",
          400: "#fc8181",
          500: "#f56565",
          600: "#b7070d",
          700: "#c53030",
          800: "#9b2c2c",
          900: "#742a2a",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blue: {
          100: "#e6faff",
          200: "#b3f0ff",
          300: "#80e6ff",
          400: "#4ddbff",
          500: "#00D4FF", // Bitcode Cyan
          600: "#00a8cc",
          700: "#007d99",
          800: "#005266",
          900: "#002933",
        },
        blueAccent: {
          100: "#002933",
          200: "#005266",
          300: "#007d99",
          400: "#00a8cc",
          500: "#00D4FF", // Bitcode Cyan
          600: "#4ddbff",
          700: "#80e6ff",
          800: "#b3f0ff",
          900: "#e6faff",
        },
        cyan: {
          100: "#e6faff",
          200: "#b3f0ff",
          300: "#80e6ff",
          400: "#4ddbff",
          500: "#00D4FF", // Bitcode Cyan (main)
          600: "#00a8cc",
          700: "#007d99",
          800: "#005266",
          900: "#002933",
        },
        orange: {
          100: "#fff3e0",
          150: "#ffe0b2",
          200: "#ffcc80",
          250: "#FF8C00", // Bitcode Orange
          300: "#ffb74d",
          400: "#ffa726",
          500: "#FF8C00", // Bitcode Orange
          600: "#cc7000",
          700: "#995400",
          800: "#663800",
          900: "#331c00",
        },
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.cyan[500], // Bitcode Cyan as secondary
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.primary[450],
            },
            background: {
              default: colors.primary[500],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[100],
            },
            secondary: {
              main: colors.cyan[500], // Bitcode Cyan as secondary
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: "#f9f9f9",
            },
          }),
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        tablet: 769,
      },
    },
    typography: {
      fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Manrope", "Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none", // Desactiva el uppercase para todos los botones
          },
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
